import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { axios, useAuth } from '../../context/AuthContext';
import { CORE_MANAGEMENT_ROLES, getCoreManagementBasePath } from '../../config/accessControl';
import CredentialsSendModals, {
  getCredentialsSendErrorMessage,
  MAX_CREDENTIAL_SEND_SELECTION,
  normalizeCredentialsSendResult,
} from '../../components/common/CredentialsSendModals';
import toast from 'react-hot-toast';

const SINAV_TURLERI = ['Sayısal', 'Eşitağırlık', 'Sözel', 'Dil', 'LGS', 'Okula Destek', 'Bursluluk', 'TYT'];
const DUZENLEME_ROLLERI = [...CORE_MANAGEMENT_ROLES, 'ogretmen'];
const EDITABLE_FIELDS = [
  'ad', 'soyad', 'sinif_id', 'sinav_turu', 'rehber_ogretmen_id',
  'telefon', 'veli_telefon', 'anne_telefon', 'baba_telefon',
  'dogum_tarihi', 'hedef_net', 'hedef_puan', 'hedef_universite', 'hedef_bolum',
];

const formatPhoneInput = (value = '') => {
  const raw = String(value);
  const digits = raw.replace(/\D/g, '');

  if (raw.trim().startsWith('+')) {
    if (digits.length <= 2) return `+${digits}`;
    const local = (digits.startsWith('90') ? digits.slice(2) : digits).slice(0, 10);
    return `+90${local ? ` ${local.slice(0, 3)}` : ''}${local.length > 3 ? ` ${local.slice(3, 6)}` : ''}${local.length > 6 ? ` ${local.slice(6, 8)}` : ''}${local.length > 8 ? ` ${local.slice(8, 10)}` : ''}`;
  }

  const limited = digits.slice(0, digits.startsWith('0') ? 11 : 10);
  const prefixLength = limited.startsWith('0') ? 4 : 3;
  return `${limited.slice(0, prefixLength)}${limited.length > prefixLength ? ` ${limited.slice(prefixLength, prefixLength + 3)}` : ''}${limited.length > prefixLength + 3 ? ` ${limited.slice(prefixLength + 3, prefixLength + 5)}` : ''}${limited.length > prefixLength + 5 ? ` ${limited.slice(prefixLength + 5, prefixLength + 7)}` : ''}`;
};

const normalizeStudentForEdit = (student = {}) => ({
  ...student,
  ad: student.ad ?? '',
  soyad: student.soyad ?? '',
  sinif_id: student.sinif_id ?? '',
  sinav_turu: student.sinav_turu ?? 'TYT',
  rehber_ogretmen_id: student.rehber_ogretmen_id ?? student.rehber_id ?? '',
  telefon: formatPhoneInput(student.telefon ?? student.ogrenci_telefon ?? ''),
  veli_telefon: formatPhoneInput(student.veli_telefon ?? ''),
  anne_telefon: formatPhoneInput(student.anne_telefon ?? ''),
  baba_telefon: formatPhoneInput(student.baba_telefon ?? ''),
  dogum_tarihi: student.dogum_tarihi ? String(student.dogum_tarihi).slice(0, 10) : '',
  hedef_net: student.hedef_net ?? '',
  hedef_puan: student.hedef_puan ?? '',
  hedef_universite: student.hedef_universite ?? '',
  hedef_bolum: student.hedef_bolum ?? '',
});

const apiErrorMessage = (error, fallback) => {
  const backendMessage = error.response?.data?.message;
  if (error.response?.status === 403) {
    return backendMessage
      ? `Bu işlem için yetkiniz bulunmuyor: ${backendMessage}`
      : 'Bu işlem için yetkiniz bulunmuyor. Lütfen yöneticinizle iletişime geçin.';
  }
  return backendMessage || fallback;
};

export default function OgrencilerPage() {
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();
  const location = useLocation();
  const [ogrenciler, setOgrenciler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [rehberler, setRehberler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arama, setArama] = useState('');
  const [sinifFilter, setSinifFilter] = useState('');
  const [sayfa, setSayfa] = useState(1);
  const [pagination, setPagination] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [showDetay, setShowDetay] = useState(null);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editOriginal, setEditOriginal] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [hardDeleteModal, setHardDeleteModal] = useState({ student: null, step: 1 });
  const [hardDeleteLoading, setHardDeleteLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [credentialsSend, setCredentialsSend] = useState({ pendingIds: [], recipientType: 'veli', sending: false, result: null });

  // '' = hepsi, '1' = sadece aktif, '0' = sadece pasif
  const [aktifFilter, setAktifFilter] = useState('');

  const canEditStudent = DUZENLEME_ROLLERI.includes(user?.rol);
  const canManageStudent = CORE_MANAGEMENT_ROLES.includes(user?.rol);

  const [form, setForm] = useState({
    ad: '', soyad: '', email: '', telefon: '', sifre: 'Ogrenci123!',
    sinif_id: '', dogum_tarihi: '', hedef_universite: '', hedef_bolum: '',
    hedef_net: 0, hedef_puan: 0, sinav_turu: 'TYT',
    veli_ad: '', veli_soyad: '', veli_telefon: '', rehber_ogretmen_id: ''
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sayfa, limit: 15 });
      if (arama) params.append('arama', arama);
      if (sinifFilter) params.append('sinif_id', sinifFilter);
      // aktifFilter '' ise parametre gönderme → backend tüm öğrencileri döner
      if (aktifFilter !== '') params.append('aktif', aktifFilter);

      const [ogrRes, sinifRes, rehberRes] = await Promise.all([
        axios.get(`/ogrenciler?${params}`),
        axios.get('/siniflar'),
        axios.get('/users?rol=rehber_ogretmen&limit=500'),
      ]);
      if (ogrRes.data.success) { setOgrenciler(ogrRes.data.data); setPagination(ogrRes.data.pagination || {}); }
      if (sinifRes.data.success) setSiniflar(sinifRes.data.data);
      if (rehberRes.data.success) setRehberler(rehberRes.data.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, 'Öğrenciler yüklenemedi.')); }
    finally { setLoading(false); }
  }, [arama, sinifFilter, aktifFilter, sayfa]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sinifId = params.get('sinif_id') || '';
    const yeni = params.get('new');
    if (sinifId) {
      setSinifFilter(sinifId);
      setForm(prev => ({ ...prev, sinif_id: sinifId }));
    }
    if (yeni === '1' && canManageStudent) setShowModal(true);
  }, [location.search, canManageStudent]);

  const handleCreate = async () => {
    if (!canManageStudent) return toast.error('Bu işlem için yetkiniz bulunmuyor.');
    if (!form.ad || !form.soyad) { toast.error('Ad ve soyad zorunlu'); return; }
    if (!form.sinif_id) { toast.error('Sınıf seçimi zorunlu'); return; }
    if (!form.veli_ad || !form.veli_soyad || !form.veli_telefon) {
      toast.error('Veli ad, soyad ve telefon zorunlu');
      return;
    }
    try {
      const payload = {
        rol: 'ogrenci',
        ad: form.ad,
        soyad: form.soyad,
        telefon: form.telefon || null,
        extra: {
          sinif_id: form.sinif_id,
          sinav_turu: form.sinav_turu,
          veli_ad: form.veli_ad,
          veli_soyad: form.veli_soyad,
          veli_telefon: form.veli_telefon,
          rehber_ogretmen_id: form.rehber_ogretmen_id || null,
          hedef_net: form.hedef_net,
          hedef_puan: form.hedef_puan,
          hedef_universite: form.hedef_universite,
          hedef_bolum: form.hedef_bolum
        },
      };
      await axios.post('/users', payload);
      toast.success('Öğrenci eklendi!');
      setShowModal(false);
      setForm({ ad: '', soyad: '', email: '', telefon: '', sifre: 'Ogrenci123!', sinif_id: '', dogum_tarihi: '', hedef_universite: '', hedef_bolum: '', hedef_net: 0, hedef_puan: 0, sinav_turu: 'TYT', veli_ad: '', veli_soyad: '', veli_telefon: '', rehber_ogretmen_id: '' });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Öğrenci eklenemedi.')); }
  };

  const openEditModal = async (student) => {
    if (!canEditStudent || editLoading) return;
    setShowDetay({ id: student.id, ad: student.ad, soyad: student.soyad });
    setEditOriginal(null);
    setEditError('');
    setEditLoading(true);
    try {
      const { data } = await axios.get(`/ogrenciler/${student.id}`);
      const detail = data?.data ?? data;
      const normalized = normalizeStudentForEdit({ ...student, ...detail, id: student.id });
      setShowDetay(normalized);
      setEditOriginal(normalized);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Öğrenci bilgileri alınamadı.'));
      setShowDetay(null);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    if (editSaving || editLoading) return;
    setShowDetay(null);
    setEditOriginal(null);
    setEditError('');
  };

  const handleEdit = async () => {
    if (!canEditStudent || !showDetay || !editOriginal || editSaving) return;

    const payload = EDITABLE_FIELDS.reduce((changed, field) => {
      const currentValue = showDetay[field] ?? '';
      const originalValue = editOriginal[field] ?? '';
      if (String(currentValue) !== String(originalValue)) {
        if (field === 'rehber_ogretmen_id' && currentValue === '') changed[field] = null;
        else changed[field] = currentValue;
      }
      return changed;
    }, {});

    if (Object.keys(payload).length === 0) {
      toast('Herhangi bir değişiklik yapılmadı.');
      return;
    }

    setEditSaving(true);
    setEditError('');
    try {
      await axios.put(`/ogrenciler/${showDetay.id}`, payload);
      toast.success('Öğrenci bilgileri güncellendi');
      setShowDetay(null);
      setEditOriginal(null);
      setEditError('');
      await load();
    } catch (e) {
      const message = apiErrorMessage(e, 'Öğrenci bilgileri güncellenemedi.');
      if (e.response?.status === 400) setEditError(message);
      toast.error(message);
    } finally {
      setEditSaving(false);
    }
  };

  // Pasife al (soft delete) — DELETE /ogrenciler/:id
  const handlePasife = async (id) => {
    if (!canManageStudent || busyAction) return;
    if (!window.confirm('Öğrenci pasife alınsın mı? Bu işlem daha sonra geri alınabilir.')) return;
    setBusyAction(`pasife:${id}`);
    try {
      await axios.delete(`/ogrenciler/${id}`);
      toast.success('Öğrenci pasife alındı');
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Öğrenci pasife alınamadı.'));
    } finally {
      setBusyAction('');
    }
  };

  // Aktife al — PATCH /ogrenciler/:id/aktife-al
  const handleAktife = async (id) => {
    if (!canManageStudent || busyAction) return;
    setBusyAction(`aktife:${id}`);
    try {
      await axios.patch(`/ogrenciler/${id}/aktife-al`);
      toast.success('Öğrenci aktife alındı');
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Öğrenci aktife alınamadı.'));
    } finally {
      setBusyAction('');
    }
  };

  // Kalıcı sil — DELETE /ogrenciler/:id/kalici
  const handleHardDelete = async () => {
    const student = hardDeleteModal.student;
    if (!canManageStudent || !student || hardDeleteModal.step !== 2 || hardDeleteLoading) return;
    setHardDeleteLoading(true);
    try {
      await axios.delete(`/ogrenciler/${student.id}/kalici`);
      toast.success('Öğrenci kalıcı olarak silindi');
      setHardDeleteModal({ student: null, step: 1 });
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Öğrenci kalıcı olarak silinemedi.'));
    } finally {
      setHardDeleteLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get('/export/ogrenciler', { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'ogrenciler.xlsx'; a.click();
      toast.success('Excel indirildi!');
    } catch (e) { toast.error(apiErrorMessage(e, 'Öğrenci listesi dışa aktarılamadı.')); }
  };

  const handleExcelSubmit = async () => {
    if (!canManageStudent) return toast.error('Bu işlem için yetkiniz bulunmuyor.');
    if (!excelFile) return toast.error('Lütfen bir Excel dosyası seçin!');
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    setIsUploading(true);
    toast.loading('Excel işleniyor, lütfen bekleyin...', { id: 'excel-upload' });
    try {
      const res = await axios.post('/ogrenciler/excel-yukle', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message || 'Öğrenciler başarıyla aktarıldı!', { id: 'excel-upload' });
      setShowExcelModal(false);
      setExcelFile(null);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Yükleme başarısız oldu.'), { id: 'excel-upload' });
    } finally {
      setIsUploading(false);
    }
  };

  const getKayitTarihi = (ogrenci) => {
    const raw =
      ogrenci?.kayit_tarihi || ogrenci?.kayitTarihi || ogrenci?.kayit_tarih ||
      ogrenci?.created_at || ogrenci?.createdAt || ogrenci?.olusturma_tarihi ||
      ogrenci?.user_created_at || ogrenci?.user?.created_at;
    if (!raw) return '—';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString('tr-TR');
  };

  // Filtre butonu etiket & renk yardımcısı
  const aktifFilterLabel = aktifFilter === '1' ? '✅ Sadece Aktif' : aktifFilter === '0' ? '🔒 Sadece Pasif' : '👁 Tümü';
  const aktifFilterBg    = aktifFilter === '0' ? '#FEF3C7' : aktifFilter === '1' ? '#D1FAE5' : 'var(--bg-elevated)';
  const aktifFilterColor = aktifFilter === '0' ? '#92400E'  : aktifFilter === '1' ? '#065F46'  : 'var(--text-muted)';

  const cycleAktifFilter = () => {
    // '' → '1' → '0' → '' döngüsü
    setAktifFilter(v => v === '' ? '1' : v === '1' ? '0' : '');
    setSayfa(1);
  };

  const isStudentSelected = (id) => selectedStudentIds.some(selectedId => String(selectedId) === String(id));
  const currentPageStudentIds = ogrenciler.map(student => student.id).filter(id => id !== undefined && id !== null);
  const isCurrentPageSelected = currentPageStudentIds.length > 0
    && currentPageStudentIds.every(id => isStudentSelected(id));

  const toggleStudentSelection = (id) => {
    if (credentialsSend.sending) return;
    if (isStudentSelected(id)) {
      setSelectedStudentIds(current => current.filter(selectedId => String(selectedId) !== String(id)));
      return;
    }
    if (selectedStudentIds.length >= MAX_CREDENTIAL_SEND_SELECTION) {
      toast.error(`Tek seferde en fazla ${MAX_CREDENTIAL_SEND_SELECTION} kayıt seçebilirsiniz.`);
      return;
    }
    setSelectedStudentIds(current => [...current, id]);
  };

  const toggleCurrentPageStudents = () => {
    if (credentialsSend.sending || currentPageStudentIds.length === 0) return;
    if (isCurrentPageSelected) {
      setSelectedStudentIds(current => current.filter(id => !currentPageStudentIds.some(pageId => String(pageId) === String(id))));
      return;
    }

    const additions = currentPageStudentIds.filter(id => !isStudentSelected(id));
    if (selectedStudentIds.length + additions.length > MAX_CREDENTIAL_SEND_SELECTION) {
      toast.error(`Tek seferde en fazla ${MAX_CREDENTIAL_SEND_SELECTION} kayıt seçebilirsiniz.`);
      return;
    }
    setSelectedStudentIds(current => [...current, ...additions]);
  };

  const openCredentialsSendConfirm = (ids) => {
    if (!canManageStudent || credentialsSend.sending) return;
    const uniqueIds = [...new Map(ids.filter(id => id !== undefined && id !== null).map(id => [String(id), id])).values()];
    if (uniqueIds.length === 0) return toast.error('Lütfen en az bir öğrenci seçin.');
    if (uniqueIds.length > MAX_CREDENTIAL_SEND_SELECTION) {
      return toast.error(`Tek seferde en fazla ${MAX_CREDENTIAL_SEND_SELECTION} kayıt seçebilirsiniz.`);
    }
    setCredentialsSend({ pendingIds: uniqueIds, recipientType: 'veli', sending: false, result: null });
  };

  const handleCredentialsSend = async () => {
    const ids = credentialsSend.pendingIds;
    if (!canManageStudent || credentialsSend.sending || ids.length === 0) return;
    setCredentialsSend(current => ({ ...current, sending: true }));
    try {
      const response = await axios.post('/ogrenciler/hesap-bilgileri-gonder', {
        ogrenci_ids: ids,
        alici_tipi: credentialsSend.recipientType || 'veli',
      });
      const result = normalizeCredentialsSendResult(response.data);
      setSelectedStudentIds([]);
      setCredentialsSend({ pendingIds: [], recipientType: credentialsSend.recipientType, sending: false, result });
      toast.success(result.message);
    } catch (error) {
      const message = getCredentialsSendErrorMessage(error);
      toast.error(message);
      setCredentialsSend(current => ({ ...current, sending: false }));
    }
  };

  return (
    <div className="ogrenciler-page-container" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        @media (max-width: 1024px) {
          .section-header { flex-direction: column; align-items: flex-start !important; gap: 15px; }
          .section-header div:last-child { width: 100%; justify-content: space-between; }
          .table-container table, .table-container thead, .table-container tbody,
          .table-container th, .table-container td, .table-container tr { display: block; }
          .table-container thead tr { position: absolute; top: -9999px; left: -9999px; }
          .table-container tr {
            border: 1px solid var(--border); border-radius: 12px; margin-bottom: 15px;
            padding: 10px; background: #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          }
          .table-container td {
            border: none; position: relative; padding-left: 45% !important;
            text-align: right !important; min-height: 40px; display: flex;
            align-items: center; justify-content: flex-end; border-bottom: 1px solid #f8f9fa;
          }
          .table-container td:last-child { border-bottom: none; }
          .table-container td:before {
            position: absolute; left: 12px; width: 40%; white-space: nowrap;
            text-align: left; font-weight: 600; color: var(--text-muted);
            content: attr(data-label); font-size: 0.75rem; text-transform: uppercase;
          }
          .table-container td div { justify-content: flex-end; }
          .modal { width: 95% !important; margin: 10px; padding: 15px !important; }
          .modal div[style*="gridTemplateColumns"] { grid-template-columns: 1fr !important; }
        }
        tr.pasif-satir { opacity: 0.65; background: #fafafa; }
      `}</style>

      {/* BAŞLIK */}
      <div className="section-header animate-fadeInUp" style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0
      }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.3rem' }}>🎓 Öğrenci Yönetimi</h2>
          <div className="section-subtitle">{pagination.total || 0} öğrenci</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-ghost btn-sm" onClick={handleExport}>📊 Dışa Aktar</button>
          {canManageStudent && (
            <>
              <button className="btn btn-outline" style={{ borderColor: '#10B981', color: '#10B981' }} onClick={() => setShowExcelModal(true)}>
                📂 Toplu Yükle
              </button>
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Yeni Öğrenci</button>
            </>
          )}
        </div>
      </div>

      {canManageStudent && selectedStudentIds.length > 0 && (
        <div className="card animate-fadeIn" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderColor: '#BFDBFE', background: '#EFF6FF' }}>
          <span style={{ color: '#1D4ED8', fontWeight: 750 }}>{selectedStudentIds.length} öğrenci seçildi</span>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-ghost btn-sm" disabled={credentialsSend.sending} onClick={() => setSelectedStudentIds([])}>Seçimi Temizle</button>
            <button className="btn btn-primary" disabled={credentialsSend.sending} onClick={() => openCredentialsSendConfirm(selectedStudentIds)}>
              {credentialsSend.sending ? 'Gönderiliyor…' : 'Seçilenlere Giriş Bilgilerini Gönder'}
            </button>
          </div>
        </div>
      )}

      {/* FİLTRELER */}
      <div className="card animate-fadeInUp stagger-1" style={{ padding: isMobile ? '14px' : '16px 20px' }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
          <input
            className="form-input"
            placeholder="🔍 Ad, soyad veya numara..."
            value={arama}
            onChange={(e) => { setArama(e.target.value); setSayfa(1); }}
            style={{ flex: 1, minWidth: isMobile ? '100%' : '200px' }}
          />
          <select
            className="form-select"
            value={sinifFilter}
            onChange={e => { setSinifFilter(e.target.value); setSayfa(1); }}
            style={{ minWidth: isMobile ? '100%' : '150px', flex: 1 }}
          >
            <option value="">Tüm Sınıflar</option>
            {(siniflar || []).map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
          </select>

          {/* Aktif / Pasif / Tümü döngüsel filtre */}
          <button
            className="btn btn-sm"
            style={{
              background: aktifFilterBg,
              color: aktifFilterColor,
              border: '1px solid var(--border)',
              whiteSpace: 'nowrap',
              fontWeight: 600,
            }}
            onClick={cycleAktifFilter}
            title="Tıklayarak: Tümü → Sadece Aktif → Sadece Pasif → Tümü"
          >
            {aktifFilterLabel}
          </button>
        </div>
      </div>

      {/* TABLO */}
      <div className="card animate-fadeInUp stagger-2" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                {canManageStudent && (
                  <th style={{ width: 44, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      aria-label="Bu sayfadaki öğrencileri seç"
                      checked={isCurrentPageSelected}
                      disabled={credentialsSend.sending || currentPageStudentIds.length === 0}
                      onChange={toggleCurrentPageStudents}
                    />
                  </th>
                )}
                <th>Öğrenci</th>
                <th>Numara</th>
                <th>Sınıf</th>
                <th>Sınav</th>
                <th>Hedef Net</th>
                <th>Veli</th>
                <th>Kayıt</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <tr key={i}>
                    {Array(canManageStudent ? 9 : 8).fill(0).map((_, j) => (
                      <td key={j}><div className="skeleton" style={{ height: 18, borderRadius: 6, width: '100%' }} /></td>
                    ))}
                  </tr>
                ))
              ) : ogrenciler.length === 0 ? (
                <tr><td colSpan={canManageStudent ? 9 : 8}>
                  <div className="empty-state" style={{ padding: 40, textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem' }}>🔍</div>
                    <h3>Öğrenci bulunamadı</h3>
                  </div>
                </td></tr>
              ) : ogrenciler.map((o, i) => {
                const isPasif = o.aktif == 0;
                return (
                  <tr key={i} className={isPasif ? 'pasif-satir' : ''}>
                    {canManageStudent && (
                      <td data-label="Seç" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          aria-label={`${o.ad} ${o.soyad} öğrencisini seç`}
                          checked={isStudentSelected(o.id)}
                          disabled={credentialsSend.sending}
                          onChange={() => toggleStudentSelection(o.id)}
                        />
                      </td>
                    )}
                    <td data-label="Öğrenci">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          className="avatar avatar-sm"
                          style={{
                            background: isPasif ? 'rgba(156,163,175,0.2)' : 'rgba(6,182,212,0.15)',
                            color: isPasif ? '#9CA3AF' : '#06B6D4',
                            flexShrink: 0
                          }}
                        >
                          {o.ad?.[0]}{o.soyad?.[0]}
                        </div>
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {o.ad} {o.soyad}
                            {isPasif && (
                              <span className="badge" style={{ background: '#FEF3C7', color: '#92400E', fontSize: '0.65rem' }}>
                                PASİF
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{o.telefon || o.email}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Numara"><span className="badge badge-muted">{o.ogrenci_no}</span></td>
                    <td data-label="Sınıf">{o.sinif_adi || '—'}</td>
                    <td data-label="Sınav"><span className="badge badge-info">{o.sinav_turu}</span></td>
                    <td data-label="Hedef Net">
                      <span style={{ fontWeight: 700, color: 'var(--accent)' }}>{o.hedef_net > 0 ? o.hedef_net : '—'}</span>
                    </td>
                    <td data-label="Veli">
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 600 }}>{o.veli_ad ? `${o.veli_ad} ${o.veli_soyad || ''}` : '—'}</div>
                        {o.veli_telefon && <div style={{ fontSize: '0.72rem' }}>📞 {o.veli_telefon}</div>}
                      </div>
                    </td>
                    <td data-label="Kayıt">{getKayitTarihi(o)}</td>
                    <td data-label="İşlem">
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {/* Detay */}
                        <button
                          className="btn btn-outline btn-sm"
                          title="Detay"
                          onClick={() => {
                            const basePath = location.pathname.startsWith('/ogretmen')
                              ? '/ogretmen'
                              : getCoreManagementBasePath(user?.rol);
                            navigate(`${basePath}/ogrenciler/${o.id}`);
                          }}
                        >🔍</button>

                        {canManageStudent && (
                          <button
                            className="btn btn-outline btn-sm"
                            title="Giriş Bilgilerini Gönder"
                            disabled={credentialsSend.sending}
                            onClick={() => openCredentialsSendConfirm([o.id])}
                          >{credentialsSend.sending && credentialsSend.pendingIds.some(id => String(id) === String(o.id)) ? 'Gönderiliyor…' : '📲 Giriş Bilgilerini Gönder'}</button>
                        )}

                        {/* Düzenle */}
                        {canEditStudent && (
                          <button
                            className="btn btn-sm"
                            title="Düzenle"
                            style={{ background: '#EFF6FF', color: 'var(--blue)' }}
                            onClick={() => openEditModal(o)}
                            disabled={editLoading || Boolean(busyAction)}
                          >✏️ Düzenle</button>
                        )}

                        {/* Aktif / Pasif toggle */}
                        {canManageStudent && (isPasif ? (
                            <button
                              className="btn btn-sm"
                              title="Aktife al"
                              style={{ background: '#D1FAE5', color: '#065F46' }}
                              onClick={() => handleAktife(o.id)}
                              disabled={Boolean(busyAction)}
                            >{busyAction === `aktife:${o.id}` ? 'İşleniyor…' : '✅ Aktife Al'}</button>
                          ) : (
                            <button
                              className="btn btn-sm"
                              title="Pasife al"
                              style={{ background: '#FEF3C7', color: '#92400E' }}
                              onClick={() => handlePasife(o.id)}
                              disabled={Boolean(busyAction)}
                            >{busyAction === `pasife:${o.id}` ? 'İşleniyor…' : '🔒 Pasife Al'}</button>
                          ))}

                        {/* Kalıcı sil */}
                        {canManageStudent && (
                          <button
                            className="btn btn-sm"
                            title="Kalıcı sil"
                            style={{ background: 'var(--danger-dim)', color: 'var(--danger)' }}
                            onClick={() => setHardDeleteModal({ student: o, step: 1 })}
                            disabled={Boolean(busyAction) || hardDeleteLoading}
                          >🗑 Kalıcı Sil</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* SAYFALAMA */}
        {pagination.toplam_sayfa > 1 && (
          <div style={{
            padding: '14px 16px', borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 10
          }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              Sayfa {sayfa} / {pagination.toplam_sayfa} — Toplam {pagination.total} öğrenci
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" disabled={sayfa === 1} onClick={() => setSayfa(s => s - 1)}>←</button>
              <button className="btn btn-ghost btn-sm" disabled={sayfa >= pagination.toplam_sayfa} onClick={() => setSayfa(s => s + 1)}>→</button>
            </div>
          </div>
        )}
      </div>

      {/* EXCEL MODAL */}
      {showExcelModal && canManageStudent && (
        <div className="modal-backdrop" onClick={() => !isUploading && setShowExcelModal(false)}>
          <div className="modal" style={{ maxWidth: 500, width: '100%', padding: isMobile ? 16 : 24 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">📂 Excel ile Toplu Kayıt</div>
              {!isUploading && <button className="modal-close" onClick={() => setShowExcelModal(false)}>✕</button>}
            </div>
            <div style={{ padding: '10px 0' }}>
              <div style={{ background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, marginBottom: 20, fontSize: '0.85rem' }}>
                <strong>⚠️ Format:</strong> OgrenciNo | Ad | Soyad | Telefon | VeliAd | VeliSoyad | VeliTel | SinifAdi | SinavTuru | HedefNet
              </div>
              <input type="file" accept=".xlsx, .xls" className="form-input" disabled={isUploading} onChange={(e) => setExcelFile(e.target.files[0])} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexDirection: isMobile ? 'column' : 'row' }}>
              <button className="btn btn-primary" style={{ flex: 1, background: '#10B981' }} onClick={handleExcelSubmit} disabled={isUploading || !excelFile}>
                {isUploading ? 'Yükleniyor...' : '✓ Yükle'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowExcelModal(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {/* DÜZENLE MODAL */}
      {showDetay && canEditStudent && (
        <div className="modal-backdrop" onClick={closeEditModal}>
          <div className="modal" style={{ maxWidth: 640, width: '100%', padding: isMobile ? 16 : 24 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">✏️ Öğrenciyi Düzenle</h3>
              {!editSaving && !editLoading && <button className="modal-close" onClick={closeEditModal}>✕</button>}
            </div>

            {editLoading ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                Öğrenci bilgileri yükleniyor…
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, maxHeight: '62vh', overflowY: 'auto', paddingRight: 2 }}>
                  <div className="form-group">
                    <label className="form-label">Ad</label>
                    <input className="form-input" value={showDetay.ad || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, ad: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Soyad</label>
                    <input className="form-input" value={showDetay.soyad || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, soyad: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sınıf</label>
                    <select className="form-select" value={showDetay.sinif_id || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, sinif_id: e.target.value })}>
                      <option value="">Seçiniz</option>
                      {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Sınav Türü</label>
                    <select className="form-select" value={showDetay.sinav_turu || 'TYT'} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, sinav_turu: e.target.value })}>
                      {SINAV_TURLERI.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Doğum Tarihi</label>
                    <input className="form-input" type="date" value={showDetay.dogum_tarihi || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, dogum_tarihi: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rehber Öğretmen</label>
                    <select className="form-select" value={showDetay.rehber_ogretmen_id || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, rehber_ogretmen_id: e.target.value })}>
                      <option value="">Seçiniz</option>
                      {rehberler.map(r => <option key={r.id} value={r.id}>{r.ad} {r.soyad}</option>)}
                    </select>
                  </div>

                  <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.82rem', marginBottom: 10 }}>Telefon Bilgileri</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                      {[
                        ['telefon', 'Öğrenci Telefonu'],
                        ['veli_telefon', 'Veli Telefonu'],
                        ['anne_telefon', 'Anne Telefonu'],
                        ['baba_telefon', 'Baba Telefonu'],
                      ].map(([field, label]) => (
                        <div className="form-group" key={field}>
                          <label className="form-label">{label}</label>
                          <input
                            className="form-input"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            placeholder="0532 123 45 67"
                            value={showDetay[field] || ''}
                            disabled={editSaving}
                            onChange={e => {
                              setEditError('');
                              setShowDetay({ ...showDetay, [field]: formatPhoneInput(e.target.value) });
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: 4 }}>
                      05321234567, 5321234567 veya +90 532 123 45 67 biçiminde girebilirsiniz. Boş bırakırsanız kayıtlı numara temizlenir.
                    </div>
                    {editError && (
                      <div role="alert" style={{ marginTop: 9, padding: '9px 11px', borderRadius: 8, background: 'var(--danger-dim,#fee2e2)', color: 'var(--danger,#dc2626)', fontSize: '0.78rem', fontWeight: 700 }}>
                        {editError}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Hedef Net</label>
                    <input className="form-input" type="number" value={showDetay.hedef_net ?? ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, hedef_net: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hedef Puan</label>
                    <input className="form-input" type="number" value={showDetay.hedef_puan ?? ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, hedef_puan: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hedef Üniversite</label>
                    <input className="form-input" value={showDetay.hedef_universite || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, hedef_universite: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Hedef Bölüm</label>
                    <input className="form-input" value={showDetay.hedef_bolum || ''} disabled={editSaving} onChange={e => setShowDetay({ ...showDetay, hedef_bolum: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 20, flexDirection: isMobile ? 'column' : 'row' }}>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleEdit} disabled={editSaving || editLoading}>
                    {editSaving ? 'Kaydediliyor…' : '✓ Değişiklikleri Kaydet'}
                  </button>
                  <button className="btn btn-outline" onClick={closeEditModal} disabled={editSaving}>İptal</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* KALICI SİLME MODAL */}
      {hardDeleteModal.student && canManageStudent && (
        <div className="modal-backdrop" onClick={() => !hardDeleteLoading && setHardDeleteModal({ student: null, step: 1 })}>
          <div
            className="modal"
            role="alertdialog"
            aria-modal="true"
            style={{ maxWidth: 520, width: '100%', padding: isMobile ? 18 : 26, border: '2px solid #EF4444', boxShadow: '0 24px 70px rgba(127,29,29,0.28)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: '#FEE2E2', color: '#B91C1C', display: 'grid', placeItems: 'center', fontSize: '1.35rem', flexShrink: 0 }}>⚠</div>
              <div>
                <h3 style={{ margin: 0, color: '#B91C1C', fontFamily: 'var(--font-ui)' }}>Öğrenciyi Kalıcı Olarak Sil</h3>
                <p style={{ margin: '7px 0 0', color: 'var(--text-secondary)', lineHeight: 1.55 }}>
                  <strong>{hardDeleteModal.student.ad} {hardDeleteModal.student.soyad}</strong> için bu işlem geri alınamaz. Öğrencinin bağlı kayıtları silinecektir; geçmiş çalışma programları korunacaktır.
                </p>
              </div>
            </div>

            {hardDeleteModal.step === 2 && (
              <div style={{ marginTop: 18, padding: '12px 14px', borderRadius: 10, background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', fontWeight: 700, fontSize: '0.84rem' }}>
                Son onay: Kalıcı silme isteği şimdi gönderilecek.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22, flexDirection: isMobile ? 'column-reverse' : 'row' }}>
              <button className="btn btn-outline" disabled={hardDeleteLoading} onClick={() => setHardDeleteModal({ student: null, step: 1 })}>Vazgeç</button>
              {hardDeleteModal.step === 1 ? (
                <button className="btn btn-danger" onClick={() => setHardDeleteModal(prev => ({ ...prev, step: 2 }))}>Devam Et</button>
              ) : (
                <button className="btn btn-danger" disabled={hardDeleteLoading} onClick={handleHardDelete}>
                  {hardDeleteLoading ? 'Siliniyor…' : 'Kalıcı olarak sil'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* YENİ KAYIT MODAL */}
      {showModal && canManageStudent && (
        <div className="modal-backdrop" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 700, width: '100%', padding: isMobile ? 16 : 24 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">🎓 Yeni Öğrenci</div></div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, maxHeight: '60vh', overflowY: 'auto' }}>
              {[
                { k: 'ad', l: 'Ad *' }, { k: 'soyad', l: 'Soyad *' },
                { k: 'telefon', l: 'Telefon' }, { k: 'veli_ad', l: 'Veli Ad *' },
                { k: 'veli_soyad', l: 'Veli Soyad *' }, { k: 'veli_telefon', l: 'Veli Telefon *' },
                { k: 'hedef_net', l: 'Hedef Net', t: 'number' }
              ].map(f => (
                <div className="form-group" key={f.k}>
                  <label className="form-label">{f.l}</label>
                  <input
                    className="form-input"
                    type={f.k.includes('telefon') ? 'tel' : (f.t || 'text')}
                    inputMode={f.k.includes('telefon') ? 'tel' : undefined}
                    placeholder={f.k.includes('telefon') ? '0532 123 45 67' : undefined}
                    value={form[f.k]}
                    onChange={e => setForm({ ...form, [f.k]: f.k.includes('telefon') ? formatPhoneInput(e.target.value) : e.target.value })}
                  />
                </div>
              ))}
              <div className="form-group">
                <label className="form-label">Sınıf</label>
                <select className="form-select" value={form.sinif_id} onChange={e => setForm({ ...form, sinif_id: e.target.value })}>
                  <option value="">Seçiniz</option>
                  {siniflar.map(s => <option key={s.id} value={s.id}>{s.ad}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Rehber Öğretmen</label>
                <select className="form-select" value={form.rehber_ogretmen_id} onChange={e => setForm({ ...form, rehber_ogretmen_id: e.target.value })}>
                  <option value="">Seçiniz</option>
                  {rehberler.map(r => <option key={r.id} value={r.id}>{r.ad} {r.soyad}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, flexDirection: isMobile ? 'column' : 'row' }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleCreate}>✓ Kaydet</button>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <CredentialsSendModals
        pendingIds={credentialsSend.pendingIds}
        sending={credentialsSend.sending}
        result={credentialsSend.result}
        recipientType={credentialsSend.recipientType}
        onRecipientTypeChange={recipientType => setCredentialsSend(current => ({ ...current, recipientType }))}
        showRecipientSelector
        onConfirm={handleCredentialsSend}
        onCancel={() => setCredentialsSend({ pendingIds: [], recipientType: 'veli', sending: false, result: null })}
        onResultClose={() => setCredentialsSend({ pendingIds: [], recipientType: 'veli', sending: false, result: null })}
      />
    </div>
  );
}
